import {
  ECommunityControlLabels,
  TCommunityAssetsControl,
  TCommunityAssetsProps,
} from './CommunityAssets.types';
import { EModalSize, EPredefinedModalIds } from 'src/constants/modals';
import React, { useEffect, useState } from 'react';
import { closeModal, openModal } from 'src/redux/modals/modals.slice';
import {
  selectCommunityAsset,
  selectGridMarketAsset,
} from 'src/redux/configuration/configuration.selectors';

import { AssetsInside } from 'src/components/AssetsInside';
import { BaseGenericModal } from 'src/components/BaseGenericModal';
import { BaseIcon } from 'src/components/BaseIcon';
import { InViewPositioner } from 'src/components/InViewPositioner';
import { LIBRARY_FILTERS_MAPPING } from 'src/constants/application';
import { TAssetsListWithSearchProps } from 'src/components/AssetsListWithSearch';
import { TIconNames } from 'src/components/BaseIcon/IconNames.types';
import classNames from 'classnames';
import s from './CommunityAssets.module.scss';
import { setSelectedAssetUuid } from 'src/redux/configuration/configuration.slice';
import { useAppDispatch } from 'src/redux/store';
import { useConfigurationFlowState } from 'src/hooks/useConfigurationFlowState';
import { useSelector } from 'react-redux';

export const CommunityAssets: React.FC<TCommunityAssetsProps> = () => {
  const dispatch = useAppDispatch();
  const { modalAddMoreShow } = useConfigurationFlowState();
  // redux slices
  const gridMarketAsset = useSelector(selectGridMarketAsset);
  const communityAsset = useSelector(selectCommunityAsset);
  const isSummaryOfCommunity = communityAsset?.uuid === gridMarketAsset?.uuid;

  // internal states
  const [activeFilter, setActiveFilter] = useState<TAssetsListWithSearchProps['activeFilter']>(
    !isSummaryOfCommunity ? LIBRARY_FILTERS_MAPPING.PV : LIBRARY_FILTERS_MAPPING.Area,
  );
  const [expanded, setExpanded] = useState<boolean>(false);
  const [visible, setVisible] = useState<boolean>(true);

  useEffect(() => {
    if (modalAddMoreShow) {
      dispatch(openModal(EPredefinedModalIds.MODAL_COMMUNITY_ASSETS));
    } else {
      dispatch(closeModal(EPredefinedModalIds.MODAL_COMMUNITY_ASSETS));
    }
  }, [dispatch, communityAsset, modalAddMoreShow]);

  // handlers
  const handleControlClicked = (control: TCommunityAssetsControl) => {
    switch (control.label) {
      case ECommunityControlLabels.ToggleExpand:
        return setExpanded(!expanded);
      case ECommunityControlLabels.Close:
        return dispatch(closeModal(EPredefinedModalIds.MODAL_COMMUNITY_ASSETS));
    }
  };

  const renderControls = () => {
    const iconForControl = (label: ECommunityControlLabels): TIconNames => {
      switch (label) {
        case ECommunityControlLabels.ToggleExpand:
          return expanded ? 'minus-thick' : 'expand';
        case ECommunityControlLabels.Close:
          return 'close';
      }
    };

    const communityControls: TCommunityAssetsControl[] = [
      {
        label: ECommunityControlLabels.ToggleExpand,
        icon: iconForControl(ECommunityControlLabels.ToggleExpand),
      },
      {
        label: ECommunityControlLabels.Close,
        icon: iconForControl(ECommunityControlLabels.Close),
      },
    ];

    return (
      <div className={s.viewActionsWrapper}>
        {communityControls.map((control) => (
          <button type="button" key={control.label} onClick={() => handleControlClicked(control)}>
            <BaseIcon key={control.label} icon={control.icon} size={10} />
          </button>
        ))}
      </div>
    );
  };

  return communityAsset ? (
    <InViewPositioner recalculateKey="community-assets-modal">
      <BaseGenericModal
        className={s.modal}
        inline
        size={EModalSize.M255}
        modalId={EPredefinedModalIds.MODAL_COMMUNITY_ASSETS}>
        <div className={s.container}>
          <header className={classNames(s.communityAssetsHeader, s.paddingLR)}>
            <h3>Community Assets</h3>
            {renderControls()}
          </header>
          {expanded && (
            <AssetsInside
              showAssetsCount
              showEnergyStatus
              theme="light"
              paddingClassName={s.paddingLR}
              parentUuid={communityAsset?.uuid}
              onAssetChoose={(data) => {
                if (data) {
                  dispatch(setSelectedAssetUuid(data.uuid));
                }
              }}
              onFilterChange={(v) => setActiveFilter(v.value)}
              activeFilter={activeFilter}
            />
          )}
        </div>
      </BaseGenericModal>
    </InViewPositioner>
  ) : (
    <></>
  );
};
